/*
Toolbar component for bulk image selection and download operations
Based on VehicleSelectionToolbar pattern
Autor: Assistant
Fecha: 2025-01-28
*/
import React from 'react'
import { Button, Checkbox, Flex, Text, Progress } from '@radix-ui/themes'

interface ImageSelectionToolbarProps {
  isSelectionMode: boolean
  selectedCount: number
  totalCount: number
  onToggleSelectionMode: () => void
  onSelectAll: () => void
  onSelectNone: () => void
  onBulkDownload: () => void
  canDownload: boolean
  downloadProgress?: {
    current: number
    total: number
    isDownloading: boolean
    currentImage?: string
  }
  // Bulk delete props
  onBulkDelete?: () => void
  canDelete?: boolean
  showDeleteOption?: boolean
  deleteProgress?: {
    current: number
    total: number
    isDeleting: boolean
    currentImage?: string
    errors: string[]
    successfulDeletions: string[]
  }
}

const ImageSelectionToolbar: React.FC<ImageSelectionToolbarProps> = ({
  isSelectionMode,
  selectedCount,
  totalCount,
  onToggleSelectionMode,
  onSelectAll,
  onSelectNone,
  onBulkDownload,
  canDownload,
  downloadProgress,
  onBulkDelete,
  canDelete = false,
  showDeleteOption = false,
  deleteProgress,
}) => {
  const allSelected = selectedCount === totalCount && totalCount > 0
  const isDownloading = downloadProgress?.isDownloading || false
  const isDeleting = deleteProgress?.isDeleting || false
  const isProcessing = isDownloading || isDeleting

  return (
    <div className="bg-white border rounded-lg p-4 mb-4 shadow-sm">
      <Flex justify="between" align="center" className="mb-2">
        <Flex align="center" gap="3">
          <Button
            variant={isSelectionMode ? 'solid' : 'outline'}
            onClick={onToggleSelectionMode}
            size="2"
            disabled={isProcessing}
          >
            {isSelectionMode ? <>✕ Cancelar selección</> : 'Seleccionar imágenes'}
          </Button>

          {isSelectionMode && (
            <>
              <Text size="2" color="gray">
                {selectedCount} de {totalCount} seleccionadas
              </Text>

              <Flex align="center" gap="2">
                <Checkbox
                  checked={allSelected}
                  disabled={isProcessing}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onSelectAll()
                    } else {
                      onSelectNone()
                    }
                  }}
                />
                <Text size="2">{allSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}</Text>
              </Flex>
            </>
          )}
        </Flex>

        {isSelectionMode && selectedCount > 0 && (
          <Flex gap="2">
            <Button
              onClick={onBulkDownload}
              disabled={!canDownload || isProcessing}
              size="2"
              variant="solid"
              color="blue"
            >
              {isDownloading ? (
                <>⏳ Descargando...</>
              ) : (
                <>
                  ⬇️ Descargar {selectedCount} imagen{selectedCount > 1 ? 'es' : ''}
                </>
              )}
            </Button>

            {/* Bulk Delete Button */}
            {showDeleteOption && onBulkDelete && (
              <Button
                onClick={onBulkDelete}
                disabled={!canDelete || isProcessing}
                size="2"
                variant="outline"
                color="red"
              >
                {isDeleting ? (
                  <>⏳ Eliminando...</>
                ) : (
                  <>
                    🗑️ Eliminar {selectedCount} imagen{selectedCount > 1 ? 'es' : ''}
                  </>
                )}
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      {/* Download Progress */}
      {isDownloading && downloadProgress && (
        <div className="mt-3">
          <Flex justify="between" align="center" className="mb-2">
            <Text size="2" color="gray">
              Descargando imagen {downloadProgress.current} de {downloadProgress.total}
            </Text>
            <Text size="2" color="gray">
              {Math.round((downloadProgress.current / downloadProgress.total) * 100)}%
            </Text>
          </Flex>
          <Progress value={(downloadProgress.current / downloadProgress.total) * 100} className="mb-2" />
          {downloadProgress.currentImage && (
            <Text size="1" color="gray" className="truncate">
              {downloadProgress.currentImage.split('/').pop()}
            </Text>
          )}
        </div>
      )}

      {/* Delete Progress */}
      {isDeleting && deleteProgress && (
        <div className="mt-3">
          <Flex justify="between" align="center" className="mb-2">
            <Text size="2" color="red">
              Eliminando imagen {deleteProgress.current} de {deleteProgress.total}
            </Text>
            <Text size="2" color="red">
              {Math.round((deleteProgress.current / deleteProgress.total) * 100)}%
            </Text>
          </Flex>
          <Progress value={(deleteProgress.current / deleteProgress.total) * 100} color="red" className="mb-2" />
          {deleteProgress.currentImage && (
            <Text size="1" color="gray" className="truncate">
              {deleteProgress.currentImage.split('/').pop()}
            </Text>
          )}
          {deleteProgress.errors.length > 0 && (
            <Text size="1" color="red" className="mt-1">
              {deleteProgress.errors.length} error{deleteProgress.errors.length > 1 ? 'es' : ''} durante la eliminación
            </Text>
          )}
          {deleteProgress.successfulDeletions.length > 0 && (
            <Text size="1" color="green" className="mt-1">
              {deleteProgress.successfulDeletions.length} imagen
              {deleteProgress.successfulDeletions.length > 1 ? 'es' : ''} eliminada
              {deleteProgress.successfulDeletions.length > 1 ? 's' : ''} correctamente
            </Text>
          )}
        </div>
      )}

      {isSelectionMode && !isProcessing && (
        <Text size="1" color="gray">
          Selecciona las imágenes que deseas descargar{showDeleteOption ? ' o eliminar' : ''}. Las operaciones se
          procesarán una por una automáticamente.
        </Text>
      )}

      {isDownloading && (
        <Text size="1" color="blue">
          Por favor espera mientras se descargan las imágenes. No cierres esta página.
        </Text>
      )}

      {isDeleting && (
        <Text size="1" color="red">
          Por favor espera mientras se eliminan las imágenes. No cierres esta página.
        </Text>
      )}
    </div>
  )
}

export default ImageSelectionToolbar
